/**
 * @lines98/shared/config/balance — typed config constants для game balance + operations.
 *
 * Per CLAUDE.md Hard Rule: «Все числовые константы (цены, коэффициенты, тайминги,
 * формула очков) — в config-файлах, не хардкодом в логике». Engine code (lines98.ts)
 * imports primitives from here; server reads ops-constants from here; client uses
 * the same source — so client/server can't drift на these numbers.
 *
 * Values marked `[TBD by economy-balancer]` are defaults from ADR (Brief #4 Accepted)
 * — engine and server code MUST work with these defaults; economy-balancer revisits
 * after telemetry.
 *
 * SOURCES:
 *   - ADR-0001 §A4 (config-as-data discipline)
 *   - ADR-0003 (replay storage / TTL defaults)
 *   - ADR-0004 (engine_version registry)
 *   - ADR-0005 (idempotency + rate-limit + LATE_SUBMIT_WINDOW)
 *   - core-daily-board.md §3.1 (DAILY_ROLLOVER_BOUNDARY = 21:00 UTC, owner-fixed)
 *   - anti-cheat.md §5.3 (MAX_SUBMIT_PAYLOAD_BYTES — resource-bound payload guard)
 *   - ADR-0018 (no move-count cap; byte-size guard + yield-friendly resim)
 *
 * NOT IN SCOPE:
 *   - Pricing for Pro subscription — `[TBD by Research/Balance]`, will land in
 *     `packages/shared/src/config/monetization.ts` after Pro ADR.
 */
// =============================================================================
// Engine primitives (board geometry + scoring)
// =============================================================================
/** Board size — 9×9. Owner-fixed at concept stage. */
export const N = 9;
/** Number of ball colors — 7. Owner-fixed at concept stage. */
export const COLORS = 7;
/** Initial balls placed at game start. Mirrors прототип. */
export const INITIAL_BALLS_COUNT = 5;
/** «Next» balls generated per turn (3 spawn after a move that didn't clear). */
export const NEXT_BALLS_PER_TURN = 3;
/**
 * Minimum line length to score. Owner-fixed (concept §3): «линии от 5».
 *
 * The score formula `scoreFor(n) = n*(n+1)/2 - 10` is calibrated к this floor:
 * 5→5, 6→11, 7→18, 8→26, 9→35. Changing this without updating `docs/balance/`
 * simulations is a CLAUDE.md hard-rule violation.
 */
export const MIN_LINE_LENGTH = 5;
// =============================================================================
// Daily rotation
// =============================================================================
/**
 * Daily board rotation boundary в UTC hours.
 *
 * `21:00 UTC = 00:00 МСК = 04:00 Asia/Ho_Chi_Minh`. Owner-decision 2026-05-23
 * (PROJECT.md / GLOSSARY.md). Single global axis: board and seed change for
 * everyone в one second; calendar date в Moscow and Vietnam stays aligned.
 */
export const DAILY_ROLLOVER_BOUNDARY_UTC_HOURS = 21;
/**
 * `LATE_SUBMIT_WINDOW_HOURS` — окно, в течение которого сервер принимает партии
 * `finish_reason = rollover`, поданные позже даты дня (ADR-0005 / anti-cheat.md §7.12).
 *
 * Default = 48h: client may sit offline overnight + a working day и still submit
 * an honest rollover game. `[TBD by economy-balancer]` after telemetry.
 */
export const LATE_SUBMIT_WINDOW_HOURS = 48;
// =============================================================================
// Anti-abuse: rate-limit + idempotency + max-moves
// =============================================================================
/**
 * Rate limit: max `POST /finish-game` per minute per authenticated player.
 *
 * Default 10/min (≈ 1 game every 6s — far above any honest player). Above the
 * limit → `rate_limited`, NOT consuming the daily attempt (anti-cheat.md §3.5 + AC-13).
 * `[TBD by economy-balancer]`.
 */
export const RATE_LIMIT_PER_PLAYER_PER_MIN = 10;
/**
 * Rate limit: max `POST /finish-game` per minute per IP.
 *
 * Default 100/min. Backstop against DDoS / multi-account farming from one IP.
 * Schools/coworking NAT-clusters won't usually exceed this. `[TBD by economy-balancer]`.
 */
export const RATE_LIMIT_PER_IP_PER_MIN = 100;
/**
 * Rate-limit time window (milliseconds) для `@fastify/rate-limit` `timeWindow`
 * option. Used together с `RATE_LIMIT_PER_PLAYER_PER_MIN` / `RATE_LIMIT_PER_IP_PER_MIN`.
 *
 * Default = 60 000 ms = 1 minute (per ADR-0005 §«Decision (rate-limit)» —
 * thresholds named «per minute»). Single source of truth so server routes
 * don't drift; replaces literal `60_000` previously inlined в `routes/finish-game.ts`
 * и `routes/daily-seed.ts` (per CLAUDE.md hard-rule «никаких хардкод-чисел»).
 *
 * `[TBD by economy-balancer]` — economy-balancer revisits if telemetry shows
 * honest-player damage with а 1-minute window (e.g., players blocked by NAT
 * burst). Doubling to 120 000 would require halving the cap к keep the
 * same effective rate.
 */
export const RATE_LIMIT_WINDOW_MS = 60_000;
/**
 * `idempotency_keys` TTL — how long the server remembers а `FinishGameRequest`
 * hash для retry deduplication (ADR-0005).
 *
 * Default 72h = LATE_SUBMIT_WINDOW_HOURS (48) + 24h safety. After expiry, only
 * `daily_attempts` PK (player_id, date_of_day) enforces «one game per day».
 */
export const IDEMPOTENCY_KEY_TTL_HOURS = 72;
/**
 * `audit_events` retention — how long the server keeps verification audit rows
 * (ADR-0003 / anti-cheat.md §4.3).
 *
 * Default 180d = half year — long enough к investigate player complaints, short
 * enough к keep storage tractable for solo-ops. `[TBD by economy-balancer]`.
 */
export const AUDIT_EVENTS_TTL_DAYS = 180;
/**
 * `SHORT_ID_COLLISION_RETRIES` — how many times the server re-rolls a replay
 * `short_id` on a UNIQUE-violation before giving up (ADR-0003 §«Decision (URL)»:
 * «short_id UNIQUE + retry-on-collision»).
 *
 * Default 5. With a 72-bit key (base62(crypto.randomBytes(9)); 2^72 ≈ 4.7×10^21
 * values, ADR-0003 addendum 2026-05-31) a collision is astronomically unlikely —
 * even a billion replays gives ~1×10^-13 per insert — so retries are effectively
 * never hit. 5 attempts is a generous correctness backstop; exhausting them is
 * statistically impossible. `[TBD by economy-balancer]` (no telemetry-driven
 * reason to change expected).
 */
export const SHORT_ID_COLLISION_RETRIES = 5;
/**
 * `MAX_SUBMIT_PAYLOAD_BYTES` — maximum raw HTTP body size for `POST /finish-game`,
 * in bytes.
 *
 * **A RESOURCE bound, NOT a game cap (ADR-0018).** Per owner-directive 2026-06-14
 * «БЕЗ КАПА»: there is NO maximum-moves count anywhere in validation. A game of
 * any length is replayed in full and scored honestly — the server never counts
 * moves against a constant. What the server DOES bound is how many bytes it will
 * accept and re-simulate in one request: that is an infrastructure limit (memory
 * to parse + CPU to resim + the JSONB row), independent of «how long a game is».
 * Fastify's per-route `bodyLimit` enforces it on the transport — a body larger
 * than this is rejected with `413 Payload Too Large` BEFORE JSON parsing and
 * BEFORE `replayScore` runs (zero CPU spent on an oversized payload; it never
 * lands in RAM whole). See anti-cheat.md §5.3 + ADR-0018 §«Решение».
 *
 * **Trace of the 8 MiB default (ADR-0018 §Config, provisional order-of-magnitude,
 * `[TBD by economy-balancer]`):** one move serialises to ~28–32 bytes of JSON
 * (`{"sr":3,"sc":4,"r":7,"c":1}`). Honest worst-case games:
 *   - typical daily 50–150 moves ≈ 1.5–5 KiB,
 *   - long «Classic» (~6000 pts, owner example) several thousand moves ≈ 60–150 KiB,
 *   - extreme marathon tens of thousands of moves ≈ hundreds of KiB – ~2 MiB.
 * 8 MiB is a generous ×4–80 headroom over ANY realistic honest game, so an honest
 * marathoner NEVER hits it, while an absurd «flood the server» payload (tens–hundreds
 * of MiB) is still rejected at the transport. Erring high is safe (DoS is cut off
 * orders of magnitude above any honest game); erring low would reject an honest
 * marathoner (owner-prohibited). economy-balancer refines from real length
 * distribution telemetry + the worker memory/CPU budget on the host (ADR-0011).
 */
export const MAX_SUBMIT_PAYLOAD_BYTES = 8 * 1024 * 1024; // 8 MiB
/**
 * `RESIM_YIELD_BATCH_SIZE` — number of moves the yield-friendly resim wrapper
 * (`replayScoreForVersionYielding`) replays per synchronous batch before yielding
 * the Node event loop (ADR-0018 Option C, brought into immediate scope).
 *
 * **Why a yielding wrapper (ADR-0018 §Option C / Revisit #1):** the canonical
 * `replayScore` is a single synchronous loop — one very long (but within the byte
 * limit) game would block the single-threaded event loop for its whole run. The
 * wrapper replays the SAME deterministic loop in batches of this many moves and
 * awaits a `setImmediate` between batches, so other requests are served between
 * batches. The engine (`lines98.ts`) is NOT touched — the wrapper drives the
 * version-frozen `startState`/`applyMove` exactly as `replayScore` does internally,
 * so the result is bit-for-bit identical (verified by property test). The yield is
 * a scheduling concern only; it cannot change a deterministic result.
 *
 * Default 2000 — at the honest ~30 bytes/move the 8 MiB byte cap admits at most
 * ~280k moves, so a worst-case resim is ~140 batches; 2000 moves is a few ms of
 * CPU per batch (well below a frame budget) so the loop is never starved, while
 * the batch count stays small enough that per-batch scheduling overhead is
 * negligible. `[TBD by economy-balancer]` once resim p99 telemetry exists.
 */
export const RESIM_YIELD_BATCH_SIZE = 2000;
// =============================================================================
// Daily leaderboard read-endpoint (T-8)
// =============================================================================
/**
 * Default page size for `GET /daily/leaderboard` when the client omits `?limit=`.
 *
 * Default 50 — a full screen of ranks без forcing pagination for the common
 * case (top-50 of the day). `[TBD by economy-balancer]` after UX telemetry.
 */
export const DAILY_LEADERBOARD_DEFAULT_LIMIT = 50;
/**
 * Maximum page size the leaderboard endpoint will honour. Requests above this
 * are clamped down к the cap (not rejected) — protects against a client asking
 * for a 100K-row page (memory + serialization cost).
 *
 * Default 200. `[TBD by economy-balancer]`.
 */
export const DAILY_LEADERBOARD_MAX_LIMIT = 200;
/**
 * Rate limit: max `GET /daily/leaderboard` reads per minute per IP.
 *
 * Read-endpoint policy — separate (and far more generous) than the write-side
 * `RATE_LIMIT_PER_PLAYER_PER_MIN` for `POST /finish-game`. The leaderboard is
 * polled / refetched by honest clients (live board updates, tab switches), so
 * the cap is high. Default 120/min (≈ one refetch every 0.5s — well above any
 * honest UI). Backstop against scrape / DDoS per anti-cheat.md §5.1.
 * `[TBD by economy-balancer]`.
 */
export const LEADERBOARD_RATE_LIMIT_PER_IP_PER_MIN = 120;
// =============================================================================
// Replay read-endpoint (T-9)
// =============================================================================
/**
 * Rate limit: max `GET /api/replay/:short_id` reads per minute per IP.
 *
 * Read-endpoint policy — separate from the write-side `RATE_LIMIT_PER_PLAYER_PER_MIN`
 * (`POST /finish-game`). Per anti-cheat.md §5.1: this limit exists specifically
 * to notice the flood-pattern «one viewer opens many today's-daily teasers in a
 * row» — automated scraping of teaser aggregates (`score_verified`, `rank`,
 * `moves_count`) to build a map of games. Honest clients open a handful of
 * replays per session (own replay, a leader's teaser, a shared link), so the cap
 * is generous but bounded.
 *
 * Default 120/min (≈ one open every 0.5s — far above any honest viewer, but a
 * scraper hammering hundreds of short_ids/min trips it). Distinct constant from
 * the leaderboard's (honest open-rates differ between board polling and replay
 * opens). `[TBD by economy-balancer]` — anti-cheat.md §11 Q9 (teaser flood
 * threshold) revisits after telemetry.
 */
export const REPLAY_RATE_LIMIT_PER_IP_PER_MIN = 120;
// =============================================================================
// Meta «Ювелир» read-endpoints (ADR-0015 — GET /meta/state)
// =============================================================================
/**
 * Rate limit: max `GET /meta/state` reads per minute per authenticated player.
 *
 * Read-endpoint policy — the meta balance projection is refetched on the result
 * screen / workshop open / tab switches. Default 120/min (≈ one refetch every
 * 0.5s — far above any honest UI). Backstop against scrape. `[TBD by economy-balancer]`.
 */
export const META_STATE_RATE_LIMIT_PER_PLAYER_PER_MIN = 120;
/**
 * Rate limit: max meta MUTATIONS (`POST /meta/craft` + `POST /meta/upgrade`) per
 * minute per authenticated player (ADR-0015 Срез 2a).
 *
 * Write-endpoint policy — these spend currency + mutate `player_jewelry`. The
 * server validates funds + applies an idempotent CAS, so a flood can't
 * double-charge; this cap is a coarse anti-abuse backstop (an honest player
 * crafts/upgrades a handful of times per session). Default 60/min (≈ one a
 * second — generous for rapid upgrade-spamming the same item, bounded against
 * automated hammering). Distinct from the read-side `…STATE…` limit.
 * `[TBD by economy-balancer]`.
 */
export const META_MUTATION_RATE_LIMIT_PER_PLAYER_PER_MIN = 60;
// =============================================================================
// Non-daily game sessions + all-time leaderboards (ADR-0013 — extra-modes)
// =============================================================================
/**
 * `GAME_SESSION_TTL_HOURS` — sliding IDLE TTL of a server-issued non-daily game
 * session (`game_sessions` row: server CSPRNG seed bound to a `session_id`). Since
 * RP-5 (run-persistence-lifecycle.md T-B / addendum-001 Решение 2 Option A) the TTL
 * SLIDES from the last activity, NOT from creation: `expires_at` is reset to
 * `NOW() + GAME_SESSION_TTL_HOURS` on every `POST /game/heartbeat` (the client pings
 * it while saving progress + on resume). So a session expires only after this much
 * INACTIVITY — an actively-played run lives indefinitely («Pro играет доску днями»),
 * while an abandoned run is reclaimed by cleanup after the idle window.
 *
 * Default 72h (3 days of inactivity) — generous enough that a player who steps away
 * for a long weekend can still resume + submit, but bounded so abandoned sessions
 * don't accumulate forever. Was 24h (ADR-0013 §6, fixed-from-creation) — raised +
 * made sliding for «play for days» (the 24h fixed TTL killed an overnight resume →
 * `invalid_seed`). The next game takes a NEW session (`POST /game/start`); this is
 * NOT «one attempt per day» (no per-day limit for non-daily, AC-3).
 * `[owner/economy-balancer tunable]` — ops parameter (session liveness), NOT a score
 * formula; revisit by telemetry (how long real players idle mid-run).
 */
export const GAME_SESSION_TTL_HOURS = 72;
/**
 * `SESSION_HEARTBEAT_THROTTLE_MIN` — minimum minutes between `POST /game/heartbeat`
 * pings from one client (RP-5 / addendum Решение 2 Option A). The client would
 * otherwise heartbeat on every saved move — far more often than needed to keep a
 * session alive on a 72h idle TTL. Throttled to at most once per this many minutes
 * (the RESUME ping bypasses the throttle — it must land immediately to extend a
 * possibly-near-expiry session before play continues).
 *
 * Default 5 min — a heartbeat is a single-row `UPDATE … WHERE session_id` (cheap), so
 * even at one per 5 min over a multi-hour run the write load is negligible, while the
 * session stays comfortably alive (5 min ≪ 72h). `[owner/economy-balancer tunable]` —
 * ops parameter (write cadence vs liveness), NOT a score formula.
 */
export const SESSION_HEARTBEAT_THROTTLE_MIN = 5;
/**
 * Rate limit: max `POST /game/start` per minute per authenticated player.
 *
 * Default 5/min — an honest player starts at most a few non-daily games per
 * minute; 5/min is generous. Protects against start-spam (inflating
 * `game_sessions`). Reserved by ADR-0005 `RATE_LIMIT.startGame`; ADR-0013 §6
 * activates it. `[TBD by economy-balancer]`.
 */
export const RATE_LIMIT_START_GAME_PER_PLAYER_PER_MIN = 5;
/**
 * Rate limit: max `POST /game/start` per minute per IP. Default 30/min — backstop
 * against multi-account session-farming from one IP. `[TBD by economy-balancer]`.
 */
export const RATE_LIMIT_START_GAME_PER_IP_PER_MIN = 30;
/**
 * `ALL_TIME_DEFAULT_PERIOD` — the single `period` dimension value the projection
 * writes today (seasonal hook per ADR-0013 §4 — owner-decision 2026-06-02 «заложить
 * hook, не реализовывать»). Seasons add new `period` values later WITHOUT altering
 * the PK `(mode, period, player_id)`. Stable — NOT a balance-tunable number.
 */
export const ALL_TIME_DEFAULT_PERIOD = 'eternal';
/**
 * Default page size for `GET /alltime/leaderboard` when `?limit=` is omitted.
 * Mirrors `DAILY_LEADERBOARD_DEFAULT_LIMIT` (50) for UX consistency.
 * `[TBD by economy-balancer]`.
 */
export const ALL_TIME_LEADERBOARD_DEFAULT_LIMIT = 50;
/**
 * Maximum page size the all-time leaderboard endpoint honours (clamp, not reject).
 * Mirrors `DAILY_LEADERBOARD_MAX_LIMIT` (200). `[TBD by economy-balancer]`.
 */
export const ALL_TIME_LEADERBOARD_MAX_LIMIT = 200;
/**
 * `STRONG_GAME_SCORE_THRESHOLD` — порог «сильной партии» для будущей выборки
 * сильных партий из `replays` (ADR-0006rev?/ADR «task 6»: итоговый счёт КАЖДОЙ
 * server-verified партии уже персистится — seed + moves + score_verified +
 * engine_version; этот порог = единственный config-кран фильтра «сильная партия»).
 *
 * **Где используется:** будущая curation/admin-выборка
 * `SELECT … FROM replays WHERE score_verified >= STRONG_GAME_SCORE_THRESHOLD …`
 * (обслуживается индексом `replays_score_verified_idx`). НЕ хот-пас рендера —
 * курирование (подбор сильных реплеев на витрину/призраков/анти-чит-аудит).
 *
 * Server-truth ТОЛЬКО: фильтруется `score_verified` (пересимулированный сервером),
 * НИКОГДА client-claimed. Casual-партии (client-seed `Math.random()`) на сервер не
 * сабмитятся и сюда НЕ попадают — by design (их счёт не верифицируем, анти-чит).
 *
 * Значение — placeholder `[TBD by economy-balancer]`: «сильная» зависит от
 * распределения счётов (телеметрия после релиза). 200 ≈ длинные линии нескольких
 * партий; калибруется при появлении данных. Живёт в config (no-hardcode).
 */
export const STRONG_GAME_SCORE_THRESHOLD = 200;
// =============================================================================
// Engine versioning (ADR-0004)
// =============================================================================
/**
 * Current engine version (SemVer).
 *
 * On bump: add а new module under `packages/shared/src/engine/versions/vX.Y.Z/`
 * (per ADR-0004), update registry, do NOT remove old versions (old replays must
 * keep replaying). T-1 shipped `v1.0.0`; multi-version registry is T-11.
 *
 * **1.1.0** (ADR-0004 addendum-001, 2026-06-07) — first breaking MINOR bump:
 * `spawnNext` ghost-reroll fix (ghost-spawn-determinism.md §2.2 — a ghost can no
 * longer displace another ghost). 1.0.0 frozen in `versions/v1.0.0/`; 1.1.0 is the
 * canon clients stamp on new partitions. Old 1.0.0 replays keep replaying via the
 * frozen module (anti-cheat.md §3.6).
 */
export const CURRENT_ENGINE_VERSION = '1.1.0';
// =============================================================================
// Numeric primitives (uint width constants)
// =============================================================================
/**
 * Maximum value of an unsigned 32-bit integer. Used as the inclusive upper
 * bound for `seed` values (uint32-wide seeds keep RNG/derive output stable
 * across JS engines that may treat numbers >2^32 differently).
 */
export const UINT32_MAX = 0xff_ff_ff_ff;
// =============================================================================
// Operational constants — canonical product URL
// =============================================================================
/**
 * Canonical public base URL. Used by server-side replay-URL generation
 * (`/r/<short_id>`) and OG-tag content per ADR-0003.
 *
 * **Owner-decision 2026-05-24 (Brief #21 §6):** домен `colorlines98.online`
 * зарегистрирован (Timeweb), это канонический host продукта. Нейминг —
 * сплав двух классик жанра (Color Lines / Gamos 1992 + Lines 98 / Winlinez).
 * `lines98.online` НЕ зарегистрирован (опциональный будущий редирект).
 *
 * **Override:** на деплое сервер может переопределить через env-переменную
 * `PUBLIC_BASE_URL` (см. `packages/server/src/config/env.ts` когда T-9 / T-10
 * добавят replay-URL + OG-tag handlers). Default здесь — для dev / тестов /
 * клиент-side build, чтобы client и server не дрейфовали.
 */
export const PUBLIC_BASE_URL = 'https://colorlines98.online';
// =============================================================================
// OG-preview (replay share card) — ADR-0003 §«OG-превью» / replays-ghosts §4.3-A
// =============================================================================
/**
 * OG-card pixel dimensions. **1200×630** — the canonical OpenGraph /
 * `twitter:card=summary_large_image` size that Twitter / Telegram / Facebook /
 * VK crawlers expect for a large preview. Server renders the PNG at exactly this
 * size (Satori → resvg-js, ADR-0003 §«OG-превью» Option A). Both endpoints
 * (`GET /r/:short_id.png` image + the `og:image` meta the HTML page points at)
 * reference these so the declared and rendered size can never drift.
 */
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
/**
 * `Cache-Control: max-age` (seconds) for a **full** OG card — one year. A full
 * card renders a finished, immutable game (`replays.moves` is append-only,
 * ADR-0003 §«Хранение»), so the PNG never changes → cache forever. Paired with
 * `immutable` in the header. 31_536_000 = 365×24×3600.
 *
 * A **teaser** card (today's foreign daily) uses a SHORT, computed max-age
 * instead — `≤ (next_rollover_utc − now)` — because at rollover the same replay
 * turns playable and the card must regenerate as a full card (replays-ghosts.md
 * §4.3-A «Кэш-стратегия» / anti-cheat.md §7.15). That TTL is per-request, not a
 * constant.
 */
export const OG_FULL_CACHE_MAX_AGE_SECONDS = 31_536_000;
// =============================================================================
// Книга реплеев (replay-collection.md / ADR-0023) — ёмкость + пагинация + RL
// =============================================================================
/**
 * `REPLAY_BOOK_OTHERS_FREE_SLOTS` — бесплатная ёмкость альбома «Чужие» (ручные
 * закладки на партии ДРУГИХ игроков) на старте, ОДИНАКОВАЯ для всех игроков.
 *
 * **Только «Чужие».** Альбом «Свои» — БЕЗ ЛИМИТА (owner 2026-06-24, шахматная
 * модель): свои сыгранные партии = производное от `replays WHERE player_id = :me`,
 * без капа / без вытеснения / без закладок. Поэтому у «Своих» НЕТ кап-ключа.
 *
 * **Где живёт:** config, НЕ в БД (owner 2026-06-24 + ADR-0023): изменение free-числа
 * = правка этой константы, не миграция и не UPDATE строк. Полная ёмкость «Чужих» на
 * игрока = `REPLAY_BOOK_OTHERS_FREE_SLOTS + book_others_extra_capacity` (докупленная
 * ёмкость хранится строкой `replay_book_capacity`, default 0; платный пак — пострелиз).
 *
 * **Default 5** (owner 2026-06-24): «5 бесплатно на старте». Первое улучшение 5→10 —
 * дешёвый хук (точная цена/валюта — пострелиз; owner: «пока зафиксируй»). Книга
 * продаёт ЁМКОСТЬ кураторской подборки, НЕ хранилище — реплеи вечны (ADR-0003),
 * прямая ссылка `/r/<short_id>` работает всегда; полный альбом НИКОГДА не удаляет
 * партии/закладки (honest-framing, I-1 — p2w-нейтрально, реплеи не влияют на счёт).
 *
 * `[owner-fixed 5; докупка-цена TBD пострелиз]`.
 */
export const REPLAY_BOOK_OTHERS_FREE_SLOTS = 5;
/**
 * `REPLAY_BOOK_OWN_GUEST_CAP_PER_MODE` — сколько последних реплеев альбома «Свои»
 * видит ГОСТЬ (anonymous-mint identity) на КАЖДОЙ вкладке-режиме (Стандарт / Классика
 * / Вслепую / Доска дня) (owner-decision G-2 2026-06-24,
 * memory/project_replay_book_decisions.md §Guest-access).
 *
 * **Только гость.** Зарегистрированный игрок (есть привязка OAuth/local-credentials)
 * видит ВЕСЬ архив без лимита (шахматная модель, owner п.3): «Свои» = производное от
 * `replays WHERE player_id = :me`, без капа/вытеснения. Кап действует исключительно
 * для аноним-только-идентичности — это тизер-стена (B3), а НЕ удаление: реплеи вечны
 * (ADR-0003), прямая ссылка `/r/<short_id>` работает всегда; honest-framing
 * (никакого FOMO «без аккаунта реплей пропадёт»). Не p2w — реплеи не влияют на счёт.
 *
 * **Где живёт:** config, НЕ хардкод/БД (owner 2026-06-24 [[feedback_balance_config_day_one]]):
 * изменение числа = правка этой константы. Сервер (own-list) при гостевом запросе режет
 * выдачу до этого числа на режим + сигналит усечение флагом `guest_capped`; клиент
 * рендерит тройку + конверсионное сообщение (i18n, 12 языков).
 *
 * **Default 3** (owner G-2 verbatim: «гость видит максимум 3 последних»).
 * `[owner-fixed 3]`.
 */
export const REPLAY_BOOK_OWN_GUEST_CAP_PER_MODE = 3;
/**
 * `REPLAY_BOOK_PAGE_SIZE` — размер страницы для list-эндпоинтов Книги
 * (`GET /book/own` query-over-replays И `GET /book/others` query-over-bookmarks).
 *
 * Оба альбома пагинируются: «Свои» потенциально большой (вся история партий
 * игрока), «Чужие» расширяемы до бесконечности докупкой — list ОБЯЗАН страничиться
 * (ADR-0023). Зеркалит `DAILY_LEADERBOARD_DEFAULT_LIMIT` (50) для UX-консистентности
 * (полный экран строк без принудительной пагинации в типичном случае).
 *
 * Default 50. `[TBD by economy-balancer]` после UX-телеметрии.
 */
export const REPLAY_BOOK_PAGE_SIZE = 50;
/**
 * `REPLAY_BOOK_MAX_PAGE_SIZE` — потолок размера страницы, который Книга honour'ит
 * (запрос выше клампится вниз к капу, НЕ отклоняется — как лидерборды). Защита от
 * клиента, просящего 100K-строчную страницу (память + стоимость сериализации).
 * Зеркалит `DAILY_LEADERBOARD_MAX_LIMIT` (200). `[TBD by economy-balancer]`.
 */
export const REPLAY_BOOK_MAX_PAGE_SIZE = 200;
/**
 * Rate limit: максимум чтений list/capacity-эндпоинтов Книги (`GET /book/own`,
 * `GET /book/others`, `GET /book/capacity`) в минуту на аутентифицированного игрока.
 *
 * Read-endpoint policy (как `META_STATE_RATE_LIMIT_PER_PLAYER_PER_MIN`): Книга
 * рефетчится на открытии/переключении вкладок альбомов и режимов. Default 120/мин
 * (≈ один рефетч/0.5с — выше любого честного UI). Бэкстоп от scrape.
 * `[TBD by economy-balancer]`.
 */
export const REPLAY_BOOK_READ_RATE_LIMIT_PER_PLAYER_PER_MIN = 120;
/**
 * Rate limit: максимум МУТАЦИЙ закладок Книги (`POST /book/others/bookmark` +
 * `DELETE /book/others/bookmark/:short_id`) в минуту на игрока.
 *
 * Write-endpoint policy (как `META_MUTATION_RATE_LIMIT_PER_PLAYER_PER_MIN`):
 * закладка/удаление — дешёвый single-row UPSERT/DELETE; натур-PK `(player_id,
 * short_id)` + проверка ёмкости защищают от двойного зачёта, этот кап — грубый
 * анти-абьюз бэкстоп (честный игрок закладывает несколько партий за сессию).
 * Default 60/мин. `[TBD by economy-balancer]`.
 */
export const REPLAY_BOOK_MUTATION_RATE_LIMIT_PER_PLAYER_PER_MIN = 60;
// =============================================================================
// Convenience grouped exports (для пакетов, импортирующих «всё сразу»)
// =============================================================================
export const RATE_LIMIT = {
    finishGamePerPlayerPerMin: RATE_LIMIT_PER_PLAYER_PER_MIN,
    finishGamePerIpPerMin: RATE_LIMIT_PER_IP_PER_MIN,
    // ADR-0013 §6: POST /game/start (non-daily session registration).
    startGamePerPlayerPerMin: RATE_LIMIT_START_GAME_PER_PLAYER_PER_MIN,
    startGamePerIpPerMin: RATE_LIMIT_START_GAME_PER_IP_PER_MIN,
};
export const TTL = {
    idempotencyKeyHours: IDEMPOTENCY_KEY_TTL_HOURS,
    auditEventsDays: AUDIT_EVENTS_TTL_DAYS,
};
//# sourceMappingURL=balance.js.map